import React, {
  ForwardRefRenderFunction,
  forwardRef,
  useEffect,
  useState,
} from "react";
import * as Yup from "yup";

interface PreChecklistItem {
  name: string;
  isRequired: boolean;
}

const emptyItem: PreChecklistItem = {
  name: "",
  isRequired: false,
};

const checklistSchema = Yup.object({
  name: Yup.string().trim().required("Checklist name is required"),
  isRequired: Yup.boolean().required(),
});

const RequiredPreOnBordingChecklist: ForwardRefRenderFunction<any, any> = (
  props,
  ref,
) => {
  const [items, setItems] = useState<PreChecklistItem[]>([]);
  const [isAllowed, setIsAllowed] = useState(false);
  const [draft, setDraft] = useState<PreChecklistItem>(emptyItem);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [error, setError] = useState("");

  useEffect(() => {
    const checklist = (props?.data?.requiredPreOnBordingChecklist || []).map(
      (item: any) =>
        typeof item === "string"
          ? { name: item, isRequired: false }
          : {
              name: item?.name || "",
              isRequired: item?.isRequired === true,
            },
    );
    setItems(checklist);
    const allowed = props?.data?.isAllowedPreOnBordingSubmission === true;
    setIsAllowed(allowed);
    props?.HandleData({
      isAllowedPreOnBordingSubmission: allowed,
      requiredPreOnBordingChecklist: checklist,
    });
  }, [props?.data]);

  const updateItems = (nextItems: PreChecklistItem[]) => {
    setItems(nextItems);
    props?.HandleData({
      isAllowedPreOnBordingSubmission: isAllowed,
      requiredPreOnBordingChecklist: nextItems,
    });
  };

  const saveDraft = async () => {
    try {
      const validDraft = {
        ...draft,
        name: draft.name.trim(),
      };
      await checklistSchema.validate(validDraft);
      const nextItems = [...items];
      if (editingIndex === null) {
        nextItems.push(validDraft);
      } else {
        nextItems[editingIndex] = validDraft;
      }
      updateItems(nextItems);
      setDraft(emptyItem);
      setEditingIndex(null);
      setError("");
    } catch (validationError: any) {
      setError(validationError.message);
    }
  };

  const editItem = (index: number) => {
    setDraft({ ...items[index] });
    setEditingIndex(index);
    setError("");
  };

  const cancelEdit = () => {
    setDraft(emptyItem);
    setEditingIndex(null);
    setError("");
  };

  const deleteItem = (index: number) => {
    updateItems(items.filter((_, itemIndex) => itemIndex !== index));
    if (editingIndex === index) {
      cancelEdit();
    }
  };

  const validateData = async () => {
    const invalidItem = items.find((item) => !item.name.trim());
    if (invalidItem) {
      setError("Checklist name is required");
      return false;
    }
    props?.HandleData({
      isAllowedPreOnBordingSubmission: isAllowed,
      requiredPreOnBordingChecklist: items.map((item) => ({
        name: item.name.trim(),
        isRequired: item.isRequired === true,
      })),
    });
    setError("");
    return true;
  };

  React.useImperativeHandle(ref, () => ({ validateData }));

  return (
    <div className="space-y-5">
      <label className="flex items-center gap-2 text-sm font-semibold">
        <input
          type="checkbox"
          checked={isAllowed}
          onChange={(event) => {
            const allowed = event.target.checked;
            setIsAllowed(allowed);
            props?.HandleData({
              isAllowedPreOnBordingSubmission: allowed,
              requiredPreOnBordingChecklist: items,
            });
          }}
        />
        Activate Pre-Onboarding Checklist?
      </label>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="md:col-span-2">
          <label
            className="block text-sm font-semibold mb-1"
            htmlFor="pre-checklist-name"
          >
            Pre-onboarding task name
          </label>
          <input
            id="pre-checklist-name"
            value={draft.name}
            onChange={(event) =>
              setDraft({ ...draft, name: event.target.value })
            }
            className="border border-neutral-300 rounded-lg h-10 px-3 w-full"
            placeholder="Enter pre-onboarding task name"
          />
        </div>
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={draft.isRequired}
            onChange={(event) =>
              setDraft({ ...draft, isRequired: event.target.checked })
            }
          />
          Required
        </label>
      </div>
      {error && <p className="text-sm text-red-500">{error}</p>}
      <div className="flex gap-2">
        <button
          type="button"
          onClick={saveDraft}
          className="bg-[#0078ae] text-white px-4 py-2 rounded-lg font-semibold"
        >
          {editingIndex === null
            ? "Add pre-onboarding task"
            : "Update pre-onboarding task"}
        </button>
        {editingIndex !== null && (
          <button
            type="button"
            onClick={cancelEdit}
            className="border border-neutral-300 px-4 py-2 rounded-lg"
          >
            Cancel
          </button>
        )}
      </div>
      <div className="space-y-2">
        {items.length ? (
          items.map((item, index) => (
            <div
              key={`${item.name}-${index}`}
              className="border border-neutral-200 rounded-lg p-3 flex flex-col md:flex-row md:items-center md:justify-between gap-3"
            >
              <div>
                <div className="font-semibold">{item.name}</div>
                <div className="text-sm text-neutral-600">
                  Required: {item.isRequired ? "Yes" : "No"}
                </div>
              </div>
              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={() => editItem(index)}
                  className="text-[#0078ae] font-semibold"
                >
                  Edit
                </button>
                <button
                  type="button"
                  onClick={() => deleteItem(index)}
                  className="text-red-500 font-semibold"
                >
                  Delete
                </button>
              </div>
            </div>
          ))
        ) : (
          <div className="text-sm text-neutral-500">
            No pre-onboarding tasks added.
          </div>
        )}
      </div>
    </div>
  );
};

export default forwardRef(RequiredPreOnBordingChecklist);
